import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import { Close } from "../icons";
import {
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useGetAllStaticRoutesQuery,
} from "../../store/api";
import toast from "react-hot-toast";

export default function RoleModal({
  open,
  onClose,
  onSubmit,
  mode = "add",
  role = null,
}) {
  const { data, isLoading } = useGetAllStaticRoutesQuery();
  const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
  const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
  const [form, setForm] = useState({
    roleName: "",
    selectedRoutes: [],
  });

  const routes = useMemo(() => {
    return data?.data?.routes || [];
  }, [data]);

  useEffect(() => {
    if (open && role) {
      setForm({
        roleName: role?.roleName || role?.role || role?.name || "",
        selectedRoutes: role?.routePermissions || role?.permissions || [],
      });
      return;
    }

    if (!open) {
      setForm({ roleName: "", selectedRoutes: [] });
    }
  }, [open, role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleRoute = (route) => {
    setForm((prev) => ({
      ...prev,
      selectedRoutes: prev.selectedRoutes.includes(route)
        ? prev.selectedRoutes.filter((item) => item !== route)
        : [...prev.selectedRoutes, route],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        roleName: form.roleName,
        routePermissions: form.selectedRoutes,
      };

      if (mode === "edit" && role?.id) {
        await updateRole({ id: role.id, ...payload }).unwrap();
        toast.success("Role updated successfully!");
      } else {
        await createRole(payload).unwrap();
        toast.success("Role created successfully!");
      }

      if (onSubmit) {
        onSubmit({ ...form, permissions: form.selectedRoutes });
      }
      onClose();
    } catch (err) {
      console.error("Failed to save role:", err);
      toast.error(err?.data?.message || "Failed to save role");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={null}
      panelClassName="max-w-[620px] pt-5 pb-9 px-0  overflow-hidden"
      centered={false}
    >
      <div className="w-full bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E5E5]  pl-8 pr-7 pb-5">
          <div>
            <p className="text-xl leading-6 font-bold text-black">
              {mode === "edit" ? "Edit Role" : "Configure New Role"}
            </p>
            <p className="text-sm leading-6 text-black">
              {mode === "edit"
                ? "Update the selected routes for this role"
                : "Select routes for this role"}
            </p>
          </div>
          <button onClick={onClose} aria-label="close">
            <Close />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-8 pt-9">
          <div>
            <label className="mb-2 block text-sm leading-6  text-black">
              Role Identifier Name
            </label>
            <input
              name="roleName"
              value={form.roleName}
              onChange={handleChange}
              placeholder="Enter role name"
              className="w-full bg-[#F2F2F2] rounded-[5px]  px-2.5 py-[10px] outline-none focus:border-[#6D4E8A]"
              required

            />
          </div>

          <div>
            <label className="mb-5 block text-sm leading-6 text-[#7E7E7E]">
              Permission Manifest (0 selected)
            </label>
            <div className="max-h-[300px] overflow-y-auto">
              {isLoading ? (
                <p className="text-sm text-[#6B7280]">Loading routes...</p>
              ) : routes.length === 0 ? (
                <p className="text-sm text-[#6B7280]">No routes available</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                  {routes.map((route) => {
                    const selected = form.selectedRoutes.includes(route);
                    return (
                      <button
                        key={route}
                        type="button"
                        onClick={() => toggleRoute(route)}
                        className={`rounded-[10px] leading-6 px-3 py-2 text-sm transition ${selected
                            ? " bg-[#6D4E8A] text-white"
                            : " bg-[#F2F2F2] text-black hover:border-[#6D4E8A]"
                          }`}
                      >
                        {route}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="mt-10 ">

            <button
              type="submit"
              disabled={isCreating || isUpdating}
              className="rounded-[10px] w-full bg-[#6D4E8A] px-4 py-2.5 text-sm font-bold text-white leading-6 hover:bg-[#5f4376] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isCreating || isUpdating
                ? mode === "edit"
                  ? "Updating Role..."
                  : "Creating Role..."
                : mode === "edit"
                  ? "Save Changes"
                  : "Grant Access"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
